<template>
  <v-container class="pa-0">
    <ValidationObserver v-slot="{ handleSubmit }">
      <v-form @submit.prevent="handleSubmit(savemachine)">
        <!-- Modal Header -->
        <v-sheet color="primary" class="pa-6 d-flex align-center">
          <v-avatar size="40" color="white opacity-20" class="mr-4">
            <v-icon color="white" small>fas fa-industry</v-icon>
          </v-avatar>
          <div>
            <div class="text-h5 white--text font-weight-black">{{ machine.id > 0 ? 'Actualizar Área' : 'Nueva Área de Producción' }}</div>
            <div class="caption white--text opacity-70">Defina capacidades y estaciones de trabajo</div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="$emit('close')"><v-icon small>fas fa-times</v-icon></v-btn>
        </v-sheet>

        <v-card-text class="pa-8">
          <v-row>
            <v-col cols="12">
              <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                <v-text-field
                  label="Nombre del Área"
                  v-model="machine.name"
                  outlined dense hide-details="auto"
                  class="modern-input mb-4"
                  :error-messages="errors"
                  placeholder="Ej: Impresión Digital, Corte Laser..."
                />
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="6">
              <ValidationProvider name="Capacidad" rules="required" v-slot="{ errors }">
                <v-text-field
                  label="Capacidad por Hora"
                  type="number"
                  v-model="machine.capacity"
                  outlined dense hide-details="auto"
                  class="modern-input mb-4"
                  :error-messages="errors"
                  :append-icon="machine.production_unit_m2 ? 'fas fa-vector-square' : 'fas fa-layer-group'"
                  @click:append="machine.production_unit_m2 = !machine.production_unit_m2"
                  :hint="machine.production_unit_m2 ? 'Metros Cuadrados' : 'Unidades'"
                  persistent-hint
                />
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="6">
              <ValidationProvider name="Horas" rules="required" v-slot="{ errors }">
                <v-text-field
                  label="Horas Operativas / Día"
                  type="number"
                  v-model="machine.hours_per_day"
                  outlined dense hide-details="auto"
                  class="modern-input mb-4"
                  :error-messages="errors"
                  min="1" max="24"
                />
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="6">
              <ValidationProvider name="Orden" rules="required" v-slot="{ errors }">
                <v-text-field
                  label="Orden de Visualización"
                  type="number"
                  v-model="machine.sort"
                  outlined dense hide-details="auto"
                  class="modern-input mb-4"
                  :error-messages="errors"
                />
              </ValidationProvider>
            </v-col>

            <v-col cols="12" md="6" class="d-flex align-center">
              <v-checkbox
                v-model="machine.hour_control"
                label="Control de Horas"
                dense hide-details
                color="primary"
                class="mt-0 pt-0"
              />
            </v-col>

            <v-col cols="12">
              <div class="caption grey--text font-weight-black text-uppercase mb-2">Disponibilidad Semanal</div>
              <div class="d-flex gap-4 pa-4 bg-grey-light rounded-lg border">
                <v-checkbox v-model="machine.saturday" label="Sábado" dense hide-details color="success" class="mt-0 pt-0" />
                <v-checkbox v-model="machine.sunday" label="Domingo" dense hide-details color="success" class="mt-0 pt-0" />
                <v-spacer></v-spacer>
                <div class="caption grey--text font-italic mt-1">Lunes a Viernes (Siempre activos)</div>
              </div>
            </v-col>
          </v-row>

          <!-- Equipments Section -->
          <div v-if="machine.id > 0" class="mt-8 pt-6 border-top">
            <div class="d-flex align-center mb-4">
              <div class="text-subtitle-1 font-weight-black grey--text text--darken-3">Equipos / Estaciones</div>
              <v-spacer></v-spacer>
              <v-btn small color="primary" depressed class="rounded-pill" @click="addEquipment">
                <v-icon x-small class="mr-2">fas fa-plus</v-icon> Agregar Equipo
              </v-btn>
            </div>

            <v-card outlined class="modern-card overflow-hidden" v-if="machine.equipments && machine.equipments.length > 0">
              <v-simple-table class="modern-table">
                <thead>
                  <tr>
                    <th class="text-left grey lighten-5">Nombre del Equipo</th>
                    <th class="text-center grey lighten-5" width="100">Estado</th>
                    <th class="text-center grey lighten-5" width="100">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="equipment in machine.equipments" :key="equipment.id">
                    <td class="body-2 font-weight-bold grey--text text--darken-3">{{ equipment.name }}</td>
                    <td class="text-center">
                      <v-switch
                        v-model="equipment.enabled"
                        color="success"
                        hide-details dense
                        class="d-inline-flex mt-0 pt-0"
                        @change="() => { newEquipment = equipment; saveEquipment() }"
                      />
                    </td>
                    <td class="text-center">
                      <v-btn icon x-small color="primary" class="grey lighten-4" @click="editEquipment(equipment)">
                        <v-icon x-small>fas fa-edit</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>
            </v-card>
            <div v-else class="text-center py-6 grey--text caption font-italic">No hay equipos registrados en esta área</div>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-6 bg-grey-light">
          <v-spacer></v-spacer>
          <v-btn text color="grey darken-1" class="font-weight-bold px-6" @click="$emit('close')">Cancelar</v-btn>
          <v-btn type="submit" color="primary" depressed class="font-weight-bold px-8 shadow-sm">
            <v-icon small class="mr-2">fas fa-save</v-icon> Guardar Área
          </v-btn>
        </v-card-actions>
      </v-form>
    </ValidationObserver>

    <!-- Equipment Edit Modal -->
    <v-dialog v-model="isEditingEquipment" max-width="450" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="primary darken-1" class="pa-4 d-flex align-center">
          <span class="white--text font-weight-bold">{{ newEquipment.id < 0 ? 'Agregar Equipo' : 'Editar Equipo' }}</span>
          <v-spacer></v-spacer>
          <v-btn icon x-small color="white" @click="isEditingEquipment = false"><v-icon x-small>fas fa-times</v-icon></v-btn>
        </v-sheet>
        <v-card-text class="pa-6">
          <ValidationObserver v-slot="{ handleSubmit }">
            <v-form @submit.prevent="handleSubmit(saveEquipment)">
              <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                <v-text-field
                  label="Nombre del Equipo"
                  v-model="newEquipment.name"
                  outlined dense hide-details="auto"
                  class="modern-input"
                  placeholder="Ej: Maquina 1, Plancha A..."
                  :error-messages="errors"
                >
                  <template v-slot:append>
                    <v-btn icon small type="submit" color="primary">
                      <v-icon small>fas fa-check-circle</v-icon>
                    </v-btn>
                  </template>
                </v-text-field>
              </ValidationProvider>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { createEquipment, updateEquipment } from '@/api/production'
import notifications from '@/utils/notifications'

export default {
  name: 'MachineCreateUpdate',
  props: ['machine', 'loading'],
  data() {
    return {
      newEquipment: { id: -1, name: '' },
      isEditingEquipment: false,
    }
  },
  methods: {
    savemachine() {
      this.$emit('save', this.machine)
      this.$emit('close')
    },
    addEquipment() {
      this.newEquipment = { id: -1, name: '' }
      this.isEditingEquipment = true
    },
    editEquipment(equipment) {
      this.newEquipment = { ...equipment }
      this.isEditingEquipment = true
    },
    async saveEquipment() {
      try {
        if (this.newEquipment.id < 0) {
          const res = await createEquipment({ name: this.newEquipment.name, machine: this.machine.id })
          this.machine.equipments.push(res)
          notifications.showSuccess(`Equipo ${res.name} creado`)
        } else {
          const res = await updateEquipment(this.newEquipment)
          const idx = this.machine.equipments.findIndex(e => e.id === res.id)
          if (idx !== -1) this.machine.equipments.splice(idx, 1, res)
          notifications.showSuccess(`Equipo ${res.name} actualizado`)
        }
        this.isEditingEquipment = false
      } catch (err) {
        notifications.showError('Error al guardar equipo: ' + err)
      }
    }
  }
}
</script>

<style scoped>
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; }
.bg-grey-light { background-color: #f8f9fa; }
.border-top { border-top: 1px solid rgba(0,0,0,0.08); }
.gap-4 { gap: 16px; }
.opacity-20 { background-color: rgba(255,255,255,0.2) !important; }
.opacity-70 { opacity: 0.7; }
.shadow-sm { box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important; }
</style>